import React from 'react'
import {IWuSwitcherOptions} from './types/IWuSwitcherOptions'
import {_IconTab} from './ui/_iconTab'
import {_Tab} from './ui/_tab'
import {_Toggle} from './ui/_toggle'

export type IWuSwitcherProps<T> = React.HTMLAttributes<HTMLDivElement> & {
  options: IWuSwitcherOptions<T>
  onChange?: (value: T) => void
  type?: 'tab' | 'toggle' | 'iconTab'
  disabled?: boolean
  value: T
  dir?: 'ltr' | 'rtl'
  size?: 'sm' | 'md' | 'lg'
  toggle?: boolean
}

/**
 * WuSwitcher does not support any custom design.
 */
export const WuSwitcher = React.forwardRef(
  <T,>(
    props: IWuSwitcherProps<T>,
    ref: React.Ref<HTMLDivElement>,
  ): React.JSX.Element => {
    const {
      type,
      options,
      value,
      disabled,
      dir,
      size,
      onChange,
      toggle = true,
      ...rest
    } = props
    const [tempValue, setTempValue] = React.useState<string>()

    const handleChange = (e: string): void => {
      let opt = tempValue
      if (e !== '') {
        opt = e
      } else {
        if (toggle) opt = tempValue === 'opt1' ? 'opt2' : 'opt1'
      }
      const mainValue = opt === 'opt1' ? options[0].value : options[1].value

      if (onChange) onChange(mainValue)
      setTempValue(opt)
    }

    React.useEffect(() => {
      if (!Array.isArray(options) || options.length < 2) {
        throw new Error('Options are not sufficient')
      }
      setTempValue(value === options[0].value ? 'opt1' : 'opt2')
    }, [options, value])

    if (!Array.isArray(options) || options.length < 2) {
      return <></>
    }
    switch (type) {
      case 'toggle':
        return (
          <_Toggle
            {...rest}
            ref={ref}
            options={options}
            value={tempValue || 'opt1'}
            onChange={handleChange}
            disabled={disabled}
            dir={dir}
          />
        )
      case 'iconTab':
        return (
          <_IconTab
            ref={ref}
            options={options}
            value={tempValue || 'opt1'}
            onChange={handleChange}
            disabled={disabled}
            dir={dir}
          />
        )
      default:
        return (
          <_Tab
            ref={ref}
            options={options}
            value={tempValue || 'opt1'}
            onChange={handleChange}
            disabled={disabled}
            dir={dir}
            size={size}
          />
        )
    }
  },
)

WuSwitcher.displayName = 'WuSwitcher'
